import { Platform } from 'react-native';

/**
 * CORE-932 — Web: the page layout does not follow window resize.
 *
 * react-native-web 0.21's `Dimensions` (which backs `useWindowDimensions`)
 * subscribes to size changes through exactly ONE event:
 *
 *   if (window.visualViewport) {
 *     window.visualViewport.addEventListener('resize', handleResize);
 *   } else {
 *     window.addEventListener('resize', handleResize);
 *   }
 *
 * In Chrome `window.visualViewport` exists, so RN-web listens ONLY on the
 * visual viewport. A plain window-edge drag does not reliably emit the visual
 * viewport's `resize` event, so `Dimensions.window` never updates, no
 * `useWindowDimensions` consumer re-renders, and the layout keeps its old
 * geometry — the window just clips a fixed-size render. That is exactly the
 * reported symptom («вёрстка осталась прежней, в окне обрезанная часть
 * интерфейса»), and it is app-wide; the Live room only makes it obvious because
 * its shell is locked to `100dvh` with `overflow: hidden`.
 *
 * The fix bridges the always-reliable `window` `resize` (and
 * `orientationchange`) onto the visual viewport, so RN-web's existing handler
 * fires and re-measures. When the visual viewport already emits its own resize
 * we simply get a duplicate update, which React collapses to a no-op.
 *
 * Native (iOS/Android) is a no-op — there is no DOM.
 */

type ResizeBridgeWindow = {
  addEventListener: (type: string, cb: (e: any) => void) => void;
  removeEventListener: (type: string, cb: (e: any) => void) => void;
  visualViewport?: {
    dispatchEvent?: (event: any) => boolean;
  } | null;
};

/**
 * Pure plumbing, split out so it is unit-testable in the node jest env: attach
 * `window` resize/orientation listeners that forward to the visual viewport,
 * and return a cleanup that detaches them.
 */
export function bridgeWindowResizeToVisualViewport(win: ResizeBridgeWindow): () => void {
  const forward = () => {
    const vv = win.visualViewport;
    if (vv && typeof vv.dispatchEvent === 'function') {
      vv.dispatchEvent(new Event('resize'));
    }
  };

  win.addEventListener('resize', forward);
  win.addEventListener('orientationchange', forward);

  return () => {
    win.removeEventListener('resize', forward);
    win.removeEventListener('orientationchange', forward);
  };
}

let installed = false;

/**
 * Web-only, idempotent. Installs the resize bridge against the real `window`.
 * Safe to call at module load before React mounts.
 */
export function installViewportResizeSync(): void {
  if (Platform.OS !== 'web' || installed) return;
  if (typeof window === 'undefined') return;
  installed = true;
  bridgeWindowResizeToVisualViewport(window as unknown as ResizeBridgeWindow);
}
